import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router';
import { ApiError, Banner, useApiClient, useQuery } from '@acc/web-shared';
import { CALL_STATUSES, type CallStatus } from '@acc/domain';
import { PageHead, shortDate, useAgency } from '../agency';

interface Call {
  callId: string;
  customerName: string;
  phone: string;
  reason: string;
  messageByMembershipId: string;
  ownerMembershipId: string | null;
  status: CallStatus;
  policyNumber: string | null;
  actionTaken: string | null;
  detailsNeeded: string | null;
  dueDate: string | null;
  callDate: string;
  version: number;
}

export function CallList() {
  const a = useAgency();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const [search, setSearch] = useState('');
  const q = useQuery<{ calls: Call[] }>(a.path('/calls'));
  const rows = useMemo(() => (q.data?.calls ?? []).filter((c) => (!status || c.status === status) && (!search || `${c.customerName} ${c.phone} ${c.policyNumber ?? ''}`.toLowerCase().includes(search.toLowerCase()))), [q.data, status, search]);
  return (
    <>
      <PageHead title="Phone calls"><Link className="btn primary" to={`${a.base}/calls/new`}>Log call</Link></PageHead>
      <div className="toolbar">
        <input type="search" placeholder="Search customer, phone or policy" aria-label="Search calls" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="inline" aria-label="Status" value={status} onChange={(e) => setParams(e.target.value ? { status: e.target.value } : {})}>
          <option value="">All statuses</option>{CALL_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      {q.error && <Banner tone="bad">{q.error.message}</Banner>}
      <div className="card">
        {q.data && rows.length === 0 && <div className="empty">No calls match.</div>}
        {rows.length > 0 && (
          <table className="data">
            <thead><tr><th>Customer</th><th>Reason</th><th>Status</th><th>Taken by</th><th>Assigned to</th><th>Date</th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.callId} className="link" onClick={() => nav(`${a.base}/calls/${c.callId}`)}>
                  <td><Link to={`${a.base}/calls/${c.callId}`} onClick={(e) => e.stopPropagation()}>{c.customerName}</Link><div className="muted" style={{ fontSize: 13 }}>{c.phone}</div></td>
                  <td>{c.reason.slice(0, 80)}</td>
                  <td><span className={`pill ${c.status === 'Follow Up Required' ? 'fu' : c.status === 'Completed' ? 'done' : ''}`}>{c.status}</span></td>
                  <td>{a.name(c.messageByMembershipId)}</td>
                  <td>{c.ownerMembershipId ? a.name(c.ownerMembershipId) : '—'}</td>
                  <td className="muted">{shortDate(c.callDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

const blank = { customerName: '', phone: '', reason: '', messageByMembershipId: '', ownerMembershipId: '', status: 'Outgoing' as CallStatus, policyNumber: '', actionTaken: '', detailsNeeded: '', dueDate: '' };

export function CallForm() {
  const a = useAgency();
  const { callId } = useParams();
  const api = useApiClient();
  const nav = useNavigate();
  const { refreshCounts } = useOutletContext<{ refreshCounts: () => void }>();
  const existing = useQuery<{ calls: Call[] }>(callId ? a.path('/calls') : null);
  const call = existing.data?.calls.find((c) => c.callId === callId);
  const [f, setF] = useState({ ...blank, messageByMembershipId: a.me.membershipId });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (call) setF({ customerName: call.customerName, phone: call.phone, reason: call.reason, messageByMembershipId: call.messageByMembershipId, ownerMembershipId: call.ownerMembershipId ?? '', status: call.status, policyNumber: call.policyNumber ?? '', actionTaken: call.actionTaken ?? '', detailsNeeded: call.detailsNeeded ?? '', dueDate: call.dueDate ?? '' });
  }, [call]);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    const body = { ...f, ownerMembershipId: f.ownerMembershipId || null, dueDate: f.dueDate || null };
    try {
      if (call) await api(a.path(`/calls/${call.callId}`), { method: 'PATCH', body: { ...body, version: call.version } });
      else await api(a.path('/calls'), { method: 'POST', body });
      refreshCounts();
      a.notify('ok', `Call with ${f.customerName} saved.`);
      nav(`${a.base}/calls`);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 409 ? 'Someone else changed this call. Reload the page to see their changes.' : (err as Error).message);
    } finally { setBusy(false); }
  }

  if (callId && existing.data && !call) return <Banner tone="warn">This call was not found.</Banner>;
  if (callId && !call) return <p className="muted">Loading…</p>;
  const staff = a.team.filter((m) => m.status !== 'Disabled');
  // Someone who has since left stays selectable on the calls they already have.
  const former = (id: string) => id && !staff.some((m) => m.membershipId === id) ? <option value={id}>{a.name(id)}</option> : null;
  return (
    <>
      <div className="crumbs"><Link to={`${a.base}/calls`}>Phone calls</Link> / {call ? call.customerName : 'Log call'}</div>
      <PageHead title={call ? `Call: ${call.customerName}` : 'Log a phone call'} />
      {error && <Banner tone="bad">{error}</Banner>}
      <form className="card" onSubmit={submit}>
        <div className="form-grid">
          <label className="field">Customer<input required maxLength={200} value={f.customerName} onChange={set('customerName')} /></label>
          <label className="field">Phone number<input required type="tel" maxLength={40} value={f.phone} onChange={set('phone')} /></label>
          <label className="field full">Reason for call<textarea required maxLength={2000} value={f.reason} onChange={set('reason')} /></label>
          <label className="field">Message taken by<select required value={f.messageByMembershipId} onChange={set('messageByMembershipId')}><option value="">Select…</option>{former(f.messageByMembershipId)}{staff.map((m) => <option key={m.membershipId} value={m.membershipId}>{m.displayName ?? m.email}</option>)}</select></label>
          <label className="field">Assigned to<select value={f.ownerMembershipId} onChange={set('ownerMembershipId')}><option value="">Nobody</option>{former(f.ownerMembershipId)}{staff.map((m) => <option key={m.membershipId} value={m.membershipId}>{m.displayName ?? m.email}</option>)}</select></label>
          <label className="field">Status<select required value={f.status} onChange={set('status')}>{CALL_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></label>
          <label className="field">Follow-up date<input type="date" value={f.dueDate} onChange={set('dueDate')} /></label>
          <label className="field">Policy number<input maxLength={60} value={f.policyNumber} onChange={set('policyNumber')} /></label>
          <label className="field full">Action taken<textarea maxLength={2000} value={f.actionTaken} onChange={set('actionTaken')} /></label>
          <label className="field full">Details needed<textarea maxLength={2000} value={f.detailsNeeded} onChange={set('detailsNeeded')} /></label>
        </div>
        <div className="actions"><Link className="btn" to={`${a.base}/calls`}>Cancel</Link><button className="btn primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save call'}</button></div>
      </form>
    </>
  );
}
